import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class WebsocketService {
  // @ts-ignore
  private socket: WebSocket | null = null;

  private cronExecutionTimestampSubject: Subject<any> = new Subject();
  private parimDataSubject: Subject<any> = new Subject();

  public cronExecutionTimestamp$: Observable<any> =
    this.cronExecutionTimestampSubject.asObservable();
  public parimData$: Observable<any> = this.parimDataSubject.asObservable();

  private isManualClose = false;

  constructor(private http: HttpClient) {}

  public connect(): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log("WebSocket is already connected.");
      return;
    }

    const url = environment.api.wss;
    this.socket = new WebSocket(`${url}/websocket`);
    this.isManualClose = false;

    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === "cronExecutionResponse") {
          this.cronExecutionTimestampSubject.next(message.data);
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    this.socket.onopen = () => {
      this.sendUserDataToWS();
      console.log("WebSocket connection established");
    };

    this.socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    this.socket.onclose = () => {
      console.log("WebSocket connection closed");
      if (!this.isManualClose) {
        setTimeout(() => this.connect(), environment.webSocketReconectDelay);
      }
    };
  }

  public async sendMessageForCronExecution() {
    const message = JSON.stringify({ type: "lastCronExecutionRequest" });
    await this.sendWithConnection(() => {
      this.socket?.send(message);
    });
  }

  public sendMessageForRefreshDashboard(
    accountIds: string,
    startOfDayTimestamp: number,
    endOfDayTimestamp: number,
    type: number
  ): Observable<any> {
    const subject = new Subject<any>();

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const data = localStorage.getItem("user");
      const user = JSON.parse(data!);

      const params = {
        userId: user.user.id,
        accountIds,
        startOfDayTimestamp,
        endOfDayTimestamp,
        type,
      };
      const message = JSON.stringify({ type: "parimRequest", data: params });

      const messageHandler = (event: MessageEvent) => {
        try {
          const response = JSON.parse(event.data);

          // Check message type
          if (response.type === "parimResponse") {
            subject.next(response.data); // Sending data to subscribers
            subject.complete(); // Let's wrap up the stream
            this.socket?.removeEventListener("message", messageHandler); // Removing the handler
          }
        } catch (error) {
          console.error("Error parsing WebSocket response:", error);
          subject.error(error); // Passing the error on to subscribers
        }
      };

      this.socket.addEventListener("message", messageHandler); // Add a handler
      this.socket.send(message); // Sending a message
    } else {
      console.error("WebSocket is not connected.");
      subject.error(new Error("WebSocket is not connected.")); // Reporting an error
    }

    return subject.asObservable(); // Return Observable
  }

  private sendUserDataToWS(): void {
    // Collect user data
    const data = localStorage.getItem("user");
    const user = JSON.parse(data!);

    const userData = {
      userName: user.user.userName,
      userId: user.user.id,
    };
    const message = JSON.stringify({ type: "userData", data: userData });

    this.socket!.send(message);
  }

  private waitForConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        resolve();
      } else if (this.socket?.readyState === WebSocket.CONNECTING) {
        const checkConnection = setInterval(() => {
          if (this.socket?.readyState === WebSocket.OPEN) {
            clearInterval(checkConnection);
            resolve();
          }
        }, 100);

        // Timeout for waiting for connection
        setTimeout(() => {
          clearInterval(checkConnection);
          reject(new Error("WebSocket connection timed out"));
        }, 5000);
      } else {
        reject(new Error("WebSocket is not connected"));
      }
    });
  }

  private async sendWithConnection(sendCallback: () => void): Promise<void> {
    try {
      await this.waitForConnection(); // Waiting for the WebSocket to be opened
      sendCallback(); // Execute the passed function
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  }

  public close(): void {
    if (this.socket!.readyState === WebSocket.OPEN) {
      this.isManualClose = true;
      this.socket!.close();
    } else {
      console.error("WebSocket is not connected.");
    }
  }
}
